import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplicationSummary,
  getApplications,
  updateApplicationStatus,
} from '../services/adminApi';

const applicationStatusOptions = ['Received', 'Reviewed', 'Interview', 'Offer', 'Rejected'];

const ApplicationsPage = () => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState<ApplicationSummary[]>([]);
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});

  const loadApplications = async () => {
    setError(null);
    try {
      const items = await getApplications();
      setApplications(items);
      setStatusMap(items.reduce((current, app) => ({ ...current, [app.id]: app.status }), {}));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('applications.loadFailed', 'Failed to load applications.'));
    }
  };

  useEffect(() => {
    void loadApplications();
  }, []);

  const handleStatusSave = async (id: string) => {
    const status = statusMap[id];
    if (!status) return;

    setSavingIds((current) => ({ ...current, [id]: true }));
    try {
      await updateApplicationStatus(id, status);
      setApplications((current) => current.map((app) => (app.id === id ? { ...app, status } : app)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('applications.saveStatusFailed', 'Failed to update status.'));
    } finally {
      setSavingIds((current) => ({ ...current, [id]: false }));
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{t('pages.applicationsTitle')}</h2>
        <p>{t('pages.applicationsDescription')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="admin-panel" style={{ display: 'grid', gap: '1rem' }}>
        {applications.map((application) => (
          <div key={application.id} className="admin-panel" style={{ padding: '1rem' }}>
            <h4 style={{ margin: 0 }}>{application.applicantName}</h4>
            <p className="muted" style={{ margin: '0.5rem 0' }}>
              {application.applicantEmail} • {new Date(application.submittedAt).toLocaleDateString()}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              {t('applications.jobIdLabel', 'Job ID')}: {application.jobPostingId}
            </p>
            <p style={{ margin: '0.5rem 0' }}>{application.coverLetter}</p>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>{t('applications.statusLabel')}</label>
              <select
                className="form-control"
                value={statusMap[application.id] ?? application.status}
                onChange={(e) => setStatusMap((current) => ({ ...current, [application.id]: e.target.value }))}
              >
                {applicationStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={savingIds[application.id]}
              onClick={() => void handleStatusSave(application.id)}
              style={{ marginTop: '0.75rem' }}
            >
              {savingIds[application.id] ? t('common.loading') : t('applications.saveStatus')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApplicationsPage;
